import React, { useEffect, useState } from "react";
import Navbar from "./Navbar/Navbar";
import { useAppSelector } from "../../hooks/hooks";
import { fetchAxiosToken } from "../../helpers/fetchAxiosToken";
import DatePicker, { registerLocale } from "react-datepicker";
import { es } from "date-fns/locale";
import { formatearNumero } from "../../components/helpers/formatearNumero";
import { usePriceToSelectedCurrency } from "../../hooks/usePriceToSelectedCurrency";

import { ModalHabitacion } from "../modalEdit/ModalHabitacion";
import Spinner from "../../components/helpers/Spinner";
import { useLocation } from "react-router-dom";
import useCurrencyPrices from "../../hooks/useCurrencyPrices";
import { ModalTour } from "../modalEdit/ModalTour";

interface Tour {
    id: number;
    nombre: string;
    descripcion: string;
    banner: string;
    precio_base: string;
    personas: string;
    id_hotel: number;
    hotel_tours_imagenes: any[];
}

interface huespedesProps {
    name: "mascotas" | "ninos" | "adultos";
    action: "sumar" | "restar";
}

const LandingTours = () => {
    const { tipo_moneda, id: casa_id } = useAppSelector(
        (state) => state.landing_house.house
    );
    const { SELECTED_CURRENCY } = useCurrencyPrices();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const parseDateToDDMMYYY = (dateString: string | null) => {
        if (!dateString) return undefined;
        const parts = dateString.split("-"); // Suponiendo que la fecha viene en formato "YYYY-MM-DD"
        if (parts.length !== 3) return undefined;
        return new Date(
            Number(parts[0]),
            Number(parts[1]) - 1,
            Number(parts[2])
        ); // Meses en JS van de 0-11
    };

    // Obtener los valores del query string
    let fecha_inicioParams = parseDateToDDMMYYY(
        queryParams.get("fecha_inicio")
    );
    let fecha_finalParams = parseDateToDDMMYYY(queryParams.get("fecha_final"));

    const formattedFechaInicioParams = fecha_inicioParams
        ? `${`${fecha_inicioParams.getDate()}`.padStart(2, "0")}/${`${
              fecha_inicioParams.getMonth() + 1
          }`.padStart(2, "0")}/${fecha_inicioParams.getFullYear()}`
        : undefined;

    const formattedFechaFinalParams = fecha_finalParams
        ? `${`${fecha_finalParams.getDate()}`.padStart(2, "0")}/${`${
              fecha_finalParams.getMonth() + 1
          }`.padStart(2, "0")}/${fecha_finalParams.getFullYear()}`
        : undefined;

    const adultosParams = Number(queryParams.get("adultos")) || 1;
    const ninosParams = Number(queryParams.get("ninos")) || 0;

    const [huespedes, setHuespedes] = useState({
        adultos: adultosParams,
        ninos: ninosParams,
        mascotas: 0,
    });

    const [loading, setLoading] = useState<boolean>(false);

    const [fechaQuery, setFechaQuery] = useState<{
        fecha_ingreso: string | undefined;
    }>({
        fecha_ingreso: formattedFechaInicioParams,
    });

    const [tours, setTours] = useState<Tour[]>();
    const [tourSelected, setTourSelected] = useState<Tour>();
    const [modalTour, setModalTour] = useState<boolean>(false);

    const { price_to_currency } = usePriceToSelectedCurrency();
    useEffect(() => {
        getHabitaciones();
    }, [casa_id]);

    const getHabitaciones = async () => {
        const { fecha_ingreso } = fechaQuery;
        setLoading(true);
        let query_fechas = "";

        // Verificar que las fechas no estén undefined
        if (fecha_ingreso) {
            // Convertir fechas al formato adecuado (por ejemplo, ISO)
            const fechaInicio = fecha_ingreso.split("/").reverse().join("-");

            // Verificar que la fecha de ingreso sea menor que la de salida

            // Construir el query string con las fechas
            query_fechas = `&fecha=${fechaInicio}`;
        }

        const total_huespedes = huespedes.adultos + huespedes.ninos;

        try {
            const response = await fetchAxiosToken({
                url: `/hoteles_tours/getAllByHotel/${casa_id}?query=Publicado&personas=${total_huespedes}${query_fechas}`,
                method: "get",
            });

            setTours(response);
        } catch (error) {
            console.error("Error al obtener habitaciones:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputClick = ({ name, action }: huespedesProps) => {
        if (action === "sumar") {
            switch (name) {
                case "adultos":
                    setHuespedes({
                        ...huespedes,
                        [name]: huespedes[name] + 1,
                    });
                    break;
                case "ninos":
                    setHuespedes({
                        ...huespedes,
                        [name]: huespedes[name] + 1,
                    });
                    break;

                default:
                    break;
            }
        } else {
            switch (name) {
                case "adultos":
                    setHuespedes({
                        ...huespedes,
                        [name]: Math.max(1, huespedes[name] - 1),
                    });
                    break;
                case "ninos":
                    setHuespedes({
                        ...huespedes,
                        [name]: Math.max(0, huespedes[name] - 1),
                    });
                    break;
                case "mascotas":
                    setHuespedes({
                        ...huespedes,
                        [name]: Math.max(0, huespedes[name] - 1),
                    });
                    break;
                default:
                    break;
            }
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;

        setHuespedes({
            ...huespedes,
            [event.target.name]: inputValue !== "" ? Number(inputValue) : "",
        });
    };

    const handleInputChangeFecha = (name: string) => (date: Date | null) => {
        if (date) {
            const formattedDate = `${`${date.getDate()}`.padStart(2, "0")}/${`${
                date.getMonth() + 1
            }`.padStart(2, "0")}/${date.getFullYear()}`;

            setFechaQuery({
                ...fechaQuery,
                [name]: formattedDate, // Guardar en formato "dd/MM/yyyy"
            });
        } else {
            setFechaQuery({
                ...fechaQuery,
                [name]: "", // Si no hay fecha, guardar vacío
            });
        }
    };

    const parseDate = (dateStr: string) => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split("/").map(Number);
        return new Date(year, month - 1, day);
    };

    const handleClick = (tour: Tour) => {
        setModalTour(true);
        setTourSelected(tour);
    };

    return (
        <div className="bg-white  overflow-clip h-full">
            <Navbar />
            {modalTour && (
                <ModalTour
                    modalVisible={modalTour}
                    setModalVisible={setModalTour}
                    id_tour={tourSelected?.id ?? 0}
                    fecha_ingreso={fechaQuery.fecha_ingreso || ""}
                    total_huespedes={huespedes.adultos + huespedes.ninos}
                />
            )}
            <div className="flex lg:flex-row flex-col items-start justify-start w-full gap-5 lg:gap-0">
                <div className="w-full lg:w-1/4">
                    <div className="flex flex-col items-center justify-center w-full mt-8 gap-7">
                        <div className="flex flex-col w-1/2 gap-1 mx-auto text-left">
                            <label className="text-sm text-[#1C4CBB] mb-2 font-bold">
                                Fecha
                            </label>
                            <div className="w-full grid-flow-col gap-2 ">
                                <DatePicker
                                    className="w-full p-2 font-medium text-center border border-[#1C4CBB] rounded-lg"
                                    placeholderText="Agrega la fecha"
                                    locale={es}
                                    autoComplete="off"
                                    dateFormat="dd/MM/yyyy"
                                    value={fechaQuery.fecha_ingreso}
                                    selected={
                                        fechaQuery.fecha_ingreso
                                            ? parseDate(
                                                  fechaQuery.fecha_ingreso
                                              )
                                            : null
                                    }
                                    onChange={handleInputChangeFecha(
                                        "fecha_ingreso"
                                    )}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col w-1/2 gap-1 mx-auto text-left">
                            <label className="text-sm text-[#1C4CBB] mb-2 font-bold">
                                Adultos
                            </label>
                            <div className="grid grid-cols-[20%_1fr_20%] w-full grid-flow-col gap-2">
                                <button
                                    onClick={() =>
                                        handleInputClick({
                                            action: "restar",
                                            name: "adultos",
                                        })
                                    }
                                    className="flex items-center justify-center w-full border border-[#1C4CBB] rounded-lg hover:scale-105"
                                >
                                    <svg
                                        className="w-6 h-6 text-[#1C4CBB]"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                </button>

                                <input
                                    className="w-full p-2 font-medium text-center border border-[#1C4CBB] rounded-lg"
                                    type="number"
                                    name="adultos"
                                    value={huespedes.adultos}
                                    onChange={handleInputChange}
                                />
                                <button
                                    onClick={() =>
                                        handleInputClick({
                                            action: "sumar",
                                            name: "adultos",
                                        })
                                    }
                                    className="flex items-center justify-center w-full border border-[#1C4CBB] rounded-lg hover:scale-105"
                                >
                                    <svg
                                        className="w-6 h-6 text-[#1C4CBB]"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        strokeWidth="2"
                                        stroke="currentColor"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path stroke="none" d="M0 0h24v24H0z" />
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-col w-1/2 gap-1 mx-auto text-left">
                            <label className="text-sm text-[#1C4CBB] mb-2 font-bold">
                                Niños
                            </label>
                            <div className="grid grid-cols-[20%_1fr_20%] w-full grid-flow-col gap-2">
                                <button
                                    onClick={() =>
                                        handleInputClick({
                                            action: "restar",
                                            name: "ninos",
                                        })
                                    }
                                    className="flex items-center justify-center w-full border border-[#1C4CBB] rounded-lg hover:scale-105"
                                >
                                    <svg
                                        className="w-6 h-6 text-[#1C4CBB]"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                </button>
                                <input
                                    className="w-full p-2 font-medium text-center border border-[#1C4CBB] rounded-lg"
                                    type="number"
                                    name="ninos"
                                    value={huespedes.ninos}
                                    onChange={handleInputChange}
                                />
                                <button
                                    onClick={() =>
                                        handleInputClick({
                                            action: "sumar",
                                            name: "ninos",
                                        })
                                    }
                                    className="flex items-center justify-center w-full border border-[#1C4CBB] rounded-lg hover:scale-105"
                                >
                                    <svg
                                        className="w-6 h-6 text-[#1C4CBB]"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        strokeWidth="2"
                                        stroke="currentColor"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path stroke="none" d="M0 0h24v24H0z" />
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div
                            onClick={() => getHabitaciones()}
                            className="bg-[#1C4CBB] text-white p-2 rounded-xl hover:scale-105 cursor-pointer w-1/2 mx-auto text-center"
                        >
                            Ver Disponibilidad
                        </div>
                    </div>
                </div>
                <div className=" grid 2xl:grid-cols-4 xl:grid-cols-3 md:grid-cols-2 grid-cols-1  w-full lg:w-3/4 h-full gap-8 p-4 bg-white border shadow-lg lg:py-5 lg:px-16">
                    {loading ? (
                        <>
                            <Spinner />
                        </>
                    ) : (
                        tours?.map((tour, index) => (
                            <div
                                onClick={() => handleClick(tour)}
                                key={`habitacion-${index}`}
                                className={`flex flex-col  items-start border p-4 rounded-lg shadow-lg relative w-[250px] mx-auto cursor-pointer hover:scale-105  `}
                            >
                                <div className=" relative mx-auto w-full ">
                                    <img
                                        loading="lazy"
                                        className=" h-60 w-full rounded-md swiper-lazy object-cover"
                                        alt="Imagen del producto"
                                        src={`${process.env.REACT_APP_PROXY_ARCHIVOS}/blu/v1/hoteles_tours/getImage/${tour.banner}`}
                                    />
                                </div>

                                {/* Descripción */}
                                <div
                                    className={`  flex flex-col justify-center items-start mt-2 w-full`}
                                >
                                    <div className="flex items-center justify-between w-full">
                                        <h2 className="text-base font-bold text-[#1C4CBB]">
                                            {tour.nombre}{" "}
                                        </h2>
                                    </div>

                                    <p className="mt-2 text-sm text-gray-600">
                                        {tour.descripcion.length > 50
                                            ? `${tour.descripcion.substring(
                                                  0,
                                                  50
                                              )}...`
                                            : tour.descripcion}
                                    </p>

                                    <div className="flex flex-col items-center justify-center w-full mt-3 ">
                                        <div className="flex items-center w-max gap-1.5">
                                            <div className="text-sm text-center md:text-base text-[#1C4CBB] ">
                                                {tour.personas} Personas
                                            </div>
                                            <svg
                                                className={`w-6 h-6 2xl:h-8 2xl:w-8 border border-[#1C4CBB] rounded-full`}
                                                id="Capa_2"
                                                data-name="Capa 2"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 62.33 62.33"
                                            >
                                                <g id="Portada">
                                                    <g>
                                                        <circle
                                                            className="cls-1"
                                                            cx="31.17"
                                                            cy="31.17"
                                                            r="30.67"
                                                            fill="white"
                                                            fillOpacity="100"
                                                        />
                                                        <g>
                                                            <path
                                                                fill="#1C4CBB"
                                                                className="cls-2"
                                                                d="m39.6,39.12c0,1.02,0,2.05,0,3.07,0,.34-.13.55-.43.71-2.07,1.09-4.27,1.75-6.61,1.95-3.3.28-6.39-.4-9.32-1.91-.35-.18-.52-.4-.51-.82.02-1.91,0-3.82.01-5.74.01-2.29,1.55-4.44,3.7-5.21.23-.08.39-.05.59.1,2.62,1.99,5.67,2,8.27,0,.23-.17.41-.18.66-.08,2.19.86,3.63,2.93,3.64,5.29,0,.88,0,1.75,0,2.63Z"
                                                            />
                                                            <path
                                                                fill="#1C4CBB"
                                                                className="cls-2"
                                                                d="m50.43,36.04c0,.85,0,1.7,0,2.54,0,.32-.13.53-.41.67-1.79.94-3.68,1.49-5.7,1.64-1.14.09-2.25-.01-3.37-.17-.33-.05-.44-.18-.45-.52-.01-1.4.07-2.8-.05-4.2-.17-2.05-1.12-3.65-2.77-4.85-.4-.29-.4-.28-.05-.64.41-.42.88-.75,1.41-1,.2-.1.36-.09.55.07,2.13,1.9,5.43,1.87,7.49,0,.18-.16.34-.17.55-.06,1.75.88,2.71,2.29,2.78,4.26.03.75,0,1.51,0,2.26Z"
                                                            />
                                                            <path
                                                                fill="#1C4CBB"
                                                                className="cls-2"
                                                                d="m11.91,35.81c.05-.84-.13-1.89.1-2.91.34-1.56,1.25-2.68,2.69-3.38.23-.11.39-.08.58.08,2.26,1.88,5.2,1.88,7.43,0,.21-.17.38-.2.61-.08.54.27,1,.62,1.43,1.04.23.23.23.36-.05.56-1.51,1.05-2.44,2.49-2.75,4.3-.14.8-.12,1.61-.12,2.42,0,.79-.01,1.59,0,2.38,0,.3-.1.43-.39.49-3.22.56-6.26.04-9.15-1.48-.27-.14-.38-.35-.38-.65,0-.86,0-1.72,0-2.78Z"
                                                            />
                                                            <path
                                                                fill="#1C4CBB"
                                                                className="cls-2"
                                                                d="m25.49,26.17c0-3.16,2.55-5.7,5.71-5.69,3.15,0,5.67,2.56,5.66,5.74,0,3.12-2.57,5.69-5.69,5.68-3.14,0-5.68-2.57-5.68-5.73Z"
                                                            />
                                                            <path
                                                                fill="#1C4CBB"
                                                                className="cls-2"
                                                                d="m48.16,25.31c0,2.66-2.17,4.83-4.84,4.83-2.62,0-4.81-2.21-4.81-4.83,0-2.63,2.21-4.84,4.85-4.83,2.64,0,4.8,2.18,4.8,4.83Z"
                                                            />
                                                            <path
                                                                fill="#1C4CBB"
                                                                className="cls-2"
                                                                d="m14.18,25.3c0-2.66,2.18-4.83,4.84-4.83,2.62,0,4.81,2.21,4.81,4.83,0,2.64-2.21,4.83-4.86,4.83-2.64,0-4.8-2.18-4.8-4.83Z"
                                                            />
                                                        </g>
                                                    </g>
                                                </g>
                                            </svg>
                                        </div>
                                    </div>

                                    <div className="flex flex-col items-center justify-center w-full  ">
                                        <div className="flex">
                                            <div className="p-3 md:p-4 sm:px-8 text-base text-center text-[#1C4CBB] align-middle rounded-lg ">
                                                <p className="font-light select-none">
                                                    Desde{" "}
                                                    <span className="font-semibold">{`${SELECTED_CURRENCY} ${formatearNumero(
                                                        price_to_currency(
                                                            Number(
                                                                tour.precio_base
                                                            )
                                                        )
                                                    )} `}</span>{" "}
                                                    /por persona
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default LandingTours;
